import { useCallback, useEffect, useState } from "react";

import { may, send } from "../api/auth";
import { console_ } from "../api/console";
import { AddButton } from "../design/AddLink";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { localName, translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import { whenIn } from "../system/format";

// ---------------------------------------------------------------------------
// SWEEP S11. AN EXCEPTION TO A CREDENTIAL, NAMED AND TIME-LIMITED.
//
// V8's gate refuses a booking when a blocking credential is missing at the shift's end,
// and V8 also built the way past it for the case it exists for — "the renewal is in
// progress" — with a reason from a closed list, a reference and an end date. The routes
// had no screen, so the only answers to a person mid-renewal were "not this week" or a
// database. A statutory credential cannot be waived at all; the server refuses, and its
// sentence is shown.
// ---------------------------------------------------------------------------

type Override = {
  id: string;
  credential_type_id: string;
  credential_name: string;
  reason_code: string;
  reason_label: string;
  reference: string | null;
  granted_at: string;
  expires_at: string;
  withdrawn_at: string | null;
};

type Option = { id: string; label: string };

export function CredentialExceptions({ workerId }: { workerId: string }) {
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const mayGrant = me ? may(me, "credential.override") : false;
  const [rows, setRows] = useState<Override[] | null>(null);
  const [granting, setGranting] = useState(false);
  const [types, setTypes] = useState<Option[]>([]);
  const [reasons, setReasons] = useState<Option[]>([]);
  const [typeId, setTypeId] = useState("");
  const [reason, setReason] = useState("");
  const [reference, setReference] = useState("");
  const [until, setUntil] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await send<{ overrides?: Override[] }>(console_.listOverrides(apiBase, workerId));
      setRows(res.status === 200 ? (res.body.overrides ?? []) : null);
    } catch {
      setRows(null);
    }
  }, [apiBase, workerId]);

  useEffect(() => {
    void load();
    if (!mayGrant) return;
    send<{ credential_types?: { id: string; name: string; names?: Record<string, string>; is_blocking: boolean }[] }>(console_.listCredentialTypes(apiBase))
      .then((res) =>
        setTypes((res.body.credential_types ?? []).filter((ct) => ct.is_blocking).map((ct) => ({ id: ct.id, label: localName(locale, ct) }))),
      )
      .catch(() => setTypes([]));
    send<{ override_reasons?: Option[] }>(console_.overrideReasons(apiBase))
      .then((res) => setReasons(res.body.override_reasons ?? []))
      .catch(() => setReasons([]));
  }, [apiBase, load, mayGrant, locale]);

  async function grant() {
    setBusy(true);
    setMessage(null);
    try {
      const res = await send<{ error?: { message?: string } }>(
        console_.grantOverride(apiBase, workerId, {
          credential_type_id: typeId,
          reason_code: reason,
          reference: reference.trim() === "" ? null : reference.trim(),
          expires_at: new Date(`${until}T23:59:59`).toISOString(),
        }),
      );
      if (res.status !== 201) {
        setMessage(refusal(res.body.error) ?? t("The exception could not be granted."));
        return;
      }
      setTypeId("");
      setReason("");
      setReference("");
      setUntil("");
      setGranting(false);
      await load();
    } catch {
      setMessage(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  async function withdraw(id: string) {
    setBusy(true);
    try {
      const res = await send<{ error?: { message?: string } }>(console_.withdrawOverride(apiBase, id));
      if (res.status !== 200) setMessage(refusal(res.body.error) ?? t("The exception could not be withdrawn."));
      await load();
    } finally {
      setBusy(false);
    }
  }

  const live = (rows ?? []).filter((o) => o.withdrawn_at === null && new Date(o.expires_at) > new Date());
  if (!mayGrant && live.length === 0) return null;

  return (
    <section className="panel">
      <div className="panel-head">
        <h3>{t("Exceptions")}</h3>
        {mayGrant ? <AddButton label={t("Grant an exception")} expanded={granting} onClick={() => setGranting(!granting)} /> : null}
      </div>
      <p className="muted">
        {t("A named, time-limited exception lets this person be booked while a credential is being renewed. A credential required by law cannot be excepted.")}
      </p>
      {live.length > 0 ? (
        <ul className="plain-list">
          {live.map((o) => (
            <li key={o.id} className="row">
              <StatusChip tone="attention" label={o.credential_name} />
              <span>
                {translateLabel(locale, o.reason_label)}
                {o.reference ? ` · ${o.reference}` : ""} · {t("until {when}", { when: whenIn(o.expires_at, orgZone) })}
              </span>
              {mayGrant ? (
                <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => void withdraw(o.id)}>
                  {t("Withdraw")}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">{t("No exception is in force.")}</p>
      )}
      {mayGrant && granting ? (
        <div className="stack">
          <div className="row">
            <div className="field">
              <label htmlFor="exc-type">{t("Credential")}</label>
              <select id="exc-type" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
                <option value="">{t("Choose…")}</option>
                {types.map((ct) => (
                  <option key={ct.id} value={ct.id}>
                    {ct.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="exc-reason">{t("Why")}</label>
              <select id="exc-reason" value={reason} onChange={(e) => setReason(e.target.value)}>
                <option value="">{t("Choose…")}</option>
                {reasons.map((r) => (
                  <option key={r.id} value={r.id}>
                    {translateLabel(locale, r.label)}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="exc-ref">{t("Reference")}</label>
              <input id="exc-ref" value={reference} onChange={(e) => setReference(e.target.value)} placeholder={t("Renewal receipt no.")} />
            </div>
            <div className="field">
              <label htmlFor="exc-until">{t("Until")}</label>
              <input id="exc-until" type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
            </div>
          </div>
          {message ? (
            <div className="form-message" role="status" aria-live="polite">
              <StatusChip tone="failed" label={t("Not granted")} />
              <span>{message}</span>
            </div>
          ) : null}
          <div className="row">
            <button
              type="button"
              className="btn"
              disabled={busy || !typeId || !reason || !until}
              onClick={() => void grant()}
            >
              {t("Grant the exception")}
            </button>
            <button type="button" className="btn btn-quiet" onClick={() => setGranting(false)}>
              {t("Cancel")}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
